// Performance monitoring utilities for tracking and optimizing app performance
import React from 'react';

export interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

export class PerformanceMonitor {
  private metrics: PerformanceMetric[] = [];
  private observers: PerformanceObserver[] = [];

  constructor() {
    this.initializeObservers();
  }

  /**
   * Initialize performance observers for automatic metric collection
   */
  private initializeObservers() {
    // Observe navigation timing
    if ('PerformanceObserver' in window) {
      try {
        const navigationObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === 'navigation') {
              const navEntry = entry as PerformanceNavigationTiming;
              this.recordMetric('page_load_time', navEntry.loadEventEnd - navEntry.startTime);
              this.recordMetric('dom_content_loaded', navEntry.domContentLoadedEventEnd - navEntry.startTime);
              this.recordMetric('first_paint', navEntry.responseEnd - navEntry.startTime);
            }
          });
        });
        navigationObserver.observe({ entryTypes: ['navigation'] });
        this.observers.push(navigationObserver);
      } catch (error) {
        console.warn('Navigation timing observer not supported:', error);
      }

      // Observe resource timing for API calls and asset loading
      try {
        const resourceObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            if (entry.entryType === 'resource') {
              const resourceEntry = entry as PerformanceResourceTiming;
              
              // Track API call performance
              if (resourceEntry.name.includes('appsync') || resourceEntry.name.includes('graphql')) {
                this.recordMetric('api_call_duration', resourceEntry.duration, {
                  url: resourceEntry.name,
                  method: 'GraphQL',
                });
              }
              
              // Track asset loading performance
              if (resourceEntry.name.includes('.js') || resourceEntry.name.includes('.css')) {
                this.recordMetric('asset_load_duration', resourceEntry.duration, {
                  url: resourceEntry.name,
                  type: resourceEntry.name.includes('.js') ? 'javascript' : 'css',
                });
              }
            }
          });
        });
        resourceObserver.observe({ entryTypes: ['resource'] });
        this.observers.push(resourceObserver);
      } catch (error) {
        console.warn('Resource timing observer not supported:', error);
      }

      // Observe Largest Contentful Paint (LCP)
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries[entries.length - 1];
          this.recordMetric('largest_contentful_paint', lastEntry.startTime);
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(lcpObserver);
      } catch (error) {
        console.warn('LCP observer not supported:', error);
      }

      // Observe First Input Delay (FID)
      try {
        const fidObserver = new PerformanceObserver((list) => {
          list.getEntries().forEach((entry) => {
            const fidEntry = entry as any;
            if (fidEntry.processingStart) {
              this.recordMetric('first_input_delay', fidEntry.processingStart - entry.startTime);
            }
          });
        });
        fidObserver.observe({ entryTypes: ['first-input'] });
        this.observers.push(fidObserver);
      } catch (error) {
        console.warn('FID observer not supported:', error);
      }
    }
  }

  /**
   * Record a custom performance metric
   */
  recordMetric(name: string, value: number, metadata?: Record<string, any>) {
    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      metadata,
    };

    this.metrics.push(metric);

    // Log performance issues for elderly users (slower devices)
    if (this.isPerformanceIssue(name, value)) {
      console.warn(`Performance issue detected: ${name} = ${value}ms`, metadata);
    }

    // Keep only last 100 metrics to prevent memory issues
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }
  }

  /**
   * Check if a metric indicates a performance issue
   */
  private isPerformanceIssue(name: string, value: number): boolean {
    const thresholds: Record<string, number> = {
      page_load_time: 3000, // 3 seconds
      dom_content_loaded: 2000, // 2 seconds
      api_call_duration: 1000, // 1 second
      largest_contentful_paint: 2500, // 2.5 seconds
      first_input_delay: 100, // 100ms
      component_render_time: 16, // 16ms (60fps)
    };

    return value > (thresholds[name] || Infinity);
  }

  /**
   * Get performance metrics summary
   */
  getMetricsSummary(): Record<string, { avg: number; max: number; count: number }> {
    const summary: Record<string, { avg: number; max: number; count: number }> = {};

    this.metrics.forEach((metric) => {
      if (!summary[metric.name]) {
        summary[metric.name] = { avg: 0, max: 0, count: 0 };
      }

      const current = summary[metric.name];
      current.count++;
      current.max = Math.max(current.max, metric.value);
      current.avg = (current.avg * (current.count - 1) + metric.value) / current.count;
    });

    return summary;
  }

  /**
   * Measure component render time
   */
  measureComponentRender<T>(componentName: string, renderFunction: () => T): T {
    const startTime = performance.now();
    const result = renderFunction();
    const endTime = performance.now();
    
    this.recordMetric('component_render_time', endTime - startTime, {
      component: componentName,
    });

    return result;
  }

  /**
   * Measure async operation time
   */
  async measureAsyncOperation<T>(
    operationName: string,
    operation: () => Promise<T>,
    metadata?: Record<string, any>
  ): Promise<T> {
    const startTime = performance.now();
    
    try {
      const result = await operation();
      const endTime = performance.now();
      
      this.recordMetric(`${operationName}_duration`, endTime - startTime, {
        ...metadata,
        success: true,
      });
      
      return result;
    } catch (error) {
      const endTime = performance.now();
      
      this.recordMetric(`${operationName}_duration`, endTime - startTime, {
        ...metadata,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      
      throw error;
    }
  }

  /**
   * Get Web Vitals scores
   */
  getWebVitals(): Record<string, number | null> {
    const vitals = {
      lcp: null as number | null, // Largest Contentful Paint
      fid: null as number | null, // First Input Delay
      cls: null as number | null, // Cumulative Layout Shift
    };

    // Get LCP
    const lcpMetrics = this.metrics.filter(m => m.name === 'largest_contentful_paint');
    if (lcpMetrics.length > 0) {
      vitals.lcp = lcpMetrics[lcpMetrics.length - 1].value;
    }

    // Get FID
    const fidMetrics = this.metrics.filter(m => m.name === 'first_input_delay');
    if (fidMetrics.length > 0) {
      vitals.fid = fidMetrics[0].value; // First input delay is only measured once
    }

    // CLS would need to be measured separately with layout shift observer
    // For now, we'll leave it as null

    return vitals;
  }

  /**
   * Export metrics for analysis
   */
  exportMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  /**
   * Clear all metrics
   */
  clearMetrics() {
    this.metrics = [];
  }

  /**
   * Cleanup observers
   */
  disconnect() {
    this.observers.forEach(observer => observer.disconnect());
    this.observers = [];
  }
}

// Singleton instance
let performanceMonitor: PerformanceMonitor | null = null;

export const getPerformanceMonitor = (): PerformanceMonitor => {
  if (!performanceMonitor) {
    performanceMonitor = new PerformanceMonitor();
  }
  return performanceMonitor;
};

/**
 * React hook for performance monitoring
 */
export const usePerformanceMonitor = () => {
  const monitor = getPerformanceMonitor();

  const measureRender = React.useCallback(
    <T>(componentName: string, renderFunction: () => T): T => {
      return monitor.measureComponentRender(componentName, renderFunction);
    },
    [monitor]
  );

  const measureAsync = React.useCallback(
    async <T>(
      operationName: string,
      operation: () => Promise<T>,
      metadata?: Record<string, any>
    ): Promise<T> => {
      return monitor.measureAsyncOperation(operationName, operation, metadata);
    },
    [monitor]
  );

  const recordMetric = React.useCallback(
    (name: string, value: number, metadata?: Record<string, any>) => {
      monitor.recordMetric(name, value, metadata);
    },
    [monitor]
  );

  return {
    measureRender,
    measureAsync,
    recordMetric,
    getMetrics: () => monitor.exportMetrics(),
    getSummary: () => monitor.getMetricsSummary(),
    getWebVitals: () => monitor.getWebVitals(),
  };
};